if (!customElements.get('team-logo-upload')) {
  const CHOICE_VALUES = {
    later: 'Customer will send separately',
  };

  class TeamLogoUpload extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-team-logo-input]');
      this.fileInput = this.querySelector('[data-team-logo-file]');
      this.uploadChoice = this.querySelector('[data-team-logo-upload-choice]');
      this.uploadLabel = this.querySelector('[data-team-logo-upload-label]');
      this.status = this.querySelector('[data-team-logo-status]');
      this.choices = this.querySelector('[data-team-logo-choices]');
      this.thumb = this.querySelector('[data-team-logo-thumb]');
      this.preview = this.querySelector('[data-team-logo-preview]');
      this.fileExt = this.querySelector('[data-team-logo-file-ext]');
      this.choiceInputs = Array.from(this.querySelectorAll('[data-team-logo-choice]'));
      this.panels = Array.from(this.querySelectorAll('[data-team-logo-panel]'));

      this.choiceInputs.forEach((choice) => {
        choice.addEventListener('change', () => this.setChoice(choice.value));
      });

      // The Upload Logo tile opens the file picker directly. The label also
      // re-dispatches a click to its radio; ignore that one.
      this.uploadChoice?.addEventListener('click', (event) => {
        if (event.target.matches('input')) return;
        this.fileInput?.click();
      });
      this.uploadChoice?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.fileInput?.click();
        }
      });

      this.fileInput?.addEventListener('change', () => this.upload(this.fileInput.files[0]));

      if (this.uploadChoice) {
        ['dragenter', 'dragover'].forEach((type) => {
          this.uploadChoice.addEventListener(type, (event) => {
            event.preventDefault();
            this.uploadChoice.classList.add('team-logo-upload__choice--drag');
          });
        });
        ['dragleave', 'drop'].forEach((type) => {
          this.uploadChoice.addEventListener(type, () => {
            this.uploadChoice.classList.remove('team-logo-upload__choice--drag');
          });
        });
        this.uploadChoice.addEventListener('drop', (event) => {
          event.preventDefault();
          const file = event.dataTransfer?.files?.[0];
          if (!file) return;
          this.selectChoice('upload');
          this.upload(file);
        });
      }

      this.setChoice(this.choice);
    }

    get choice() {
      return this.choiceInputs.find((c) => c.checked)?.value || '';
    }

    selectChoice(value) {
      const input = this.choiceInputs.find((c) => c.value === value);
      if (input && !input.checked) {
        input.checked = true;
        this.setChoice(value);
      }
    }

    // Restores a saved Team Logo property value (used by the builder's
    // edit-from-cart draft) back into the matching choice + upload state.
    restore(value) {
      if (!value) return;
      let choice = Object.keys(CHOICE_VALUES).find((key) => CHOICE_VALUES[key] === value);
      if (!choice) {
        // Anything else should be an uploaded file URL; skip values from
        // choices that no longer exist (e.g. an old "logo on file" draft).
        if (!/^https?:\/\//.test(value)) return;
        choice = 'upload';
      }
      if (choice === 'upload') {
        this.uploadedUrl = value;
        this.showUploaded(value, value.split('/').pop());
      }
      const input = this.choiceInputs.find((c) => c.value === choice);
      if (input) input.checked = true;
      this.setChoice(choice);
    }

    setChoice(value) {
      if (value === 'upload') {
        this.input.value = this.uploadedUrl || '';
      } else {
        this.input.value = CHOICE_VALUES[value] || '';
      }
      this.syncPanels();
      this.emitChange();
    }

    // The uploaded logo sits in the tile row, left of Replace Logo. The
    // upload panel below only appears for upload status/errors.
    syncPanels() {
      const choice = this.choice;
      this.panels.forEach((panel) => {
        const key = panel.dataset.teamLogoPanel;
        panel.hidden = key !== choice || (key === 'upload' && !this.status?.textContent);
      });

      const showThumb = choice === 'upload' && Boolean(this.uploadedUrl);
      if (this.thumb) this.thumb.hidden = !showThumb;
      this.choices?.classList.toggle('team-logo-upload__choices--with-thumb', showThumb);
    }

    emitChange() {
      this.dispatchEvent(
        new CustomEvent('caliburn:team-logo-change', {
          bubbles: true,
          detail: { choice: this.choice, value: this.input.value },
        })
      );
    }

    async upload(file) {
      if (!file) return;

      this.selectChoice('upload');
      this.setStatus('Uploading logo...', null);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch('/apps/file-uploader', { method: 'POST', body: formData });
        const data = await response.json();

        if (!data.success || !data.url) {
          this.setStatus(data.error || 'Upload failed. You can still choose Send It Later.', 'error');
          return;
        }

        this.uploadedUrl = data.url;
        this.input.value = data.url;
        this.setStatus('', null);
        this.showUploaded(data.url, file.name);
        this.syncPanels();
        this.emitChange();
      } catch (err) {
        console.error('team-logo-upload: upload failed', err);
        this.setStatus('Upload failed. You can still choose Send It Later.', 'error');
      } finally {
        if (this.fileInput) this.fileInput.value = '';
      }
    }

    showUploaded(url, fileName) {
      const ext = (fileName || '').toLowerCase().split('.').pop();
      if (this.uploadLabel) this.uploadLabel.textContent = 'Replace Logo';

      if (this.thumb) this.thumb.href = url;

      if (['png', 'jpg', 'jpeg'].includes(ext)) {
        this.preview.src = url;
        this.preview.hidden = false;
        this.fileExt.hidden = true;
      } else {
        this.preview.hidden = true;
        this.fileExt.textContent = `.${ext}`;
        this.fileExt.hidden = false;
      }
    }

    setStatus(message, kind) {
      if (!this.status) return;
      this.status.textContent = message;
      if (kind) {
        this.status.dataset.status = kind;
      } else {
        delete this.status.dataset.status;
      }
      this.syncPanels();
    }
  }

  customElements.define('team-logo-upload', TeamLogoUpload);
}
